// utils/ConsentUtils.js (или как он у тебя называется)
import React, { useEffect, useState, useCallback } from 'react';
import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_API_KEY = process.env.NEXT_PUBLIC_POSTHOG_API_KEY || 'phc_dfLSIQAGgA350UPP9HEKtSkiWIRc2E3HCIUVSs44zWo'; // Используй переменные окружения
const POSTHOG_API_HOST = process.env.NEXT_PUBLIC_POSTHOG_API_HOST || 'https://eu.i.posthog.com';

// Styling constants (без изменений)
const CONTAINER_CLASSES = "fixed bottom-4 right-4 left-4 md:left-auto z-50";
const BANNER_CLASSES = "bg-white w-full md:max-w-md rounded-xl shadow-2xl ml-auto";
// ... (остальные классы без изменений)
const BUTTON_PRIMARY_CLASSES = "bg-black text-white px-6 py-3 rounded text-sm";
const BUTTON_OUTLINE_CLASSES = "bg-white border border-black text-black px-6 py-3 rounded text-sm";
const BUTTON_SECONDARY_CLASSES = "text-black font-medium text-sm hover:underline";
const TOGGLE_BUTTON_CLASSES = "text-xs mr-2 text-gray-400 font-bold cursor-pointer w-4";


// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

// ------------------------
// Utility Functions
// ------------------------

export const getDefaultPreferences = () => ({
  essential: true, // Всегда true
  analytics: false,
  advertising: false
});

export const hasUserMadeChoice = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('consent_preferences') !== null;
};

export const getStoredPreferences = () => {
  if (typeof window === 'undefined') return null;
  const consentCookie = localStorage.getItem('consent_preferences');
  if (!consentCookie) return null;
  try {
    return JSON.parse(consentCookie);
  } catch (error) {
    console.error('[Consent] Error parsing stored preferences:', error);
    return null;
  }
};


export const hasConsent = (type) => {
  if (typeof window === 'undefined') return false; // На сервере нет согласия
  const preferences = getStoredPreferences();
  // Если нет сохраненных предпочтений, считаем, что согласия нет (кроме essential)
  if (!preferences) return type === 'essential';
  return preferences[type] === true;
};

export const getAllConsents = () => {
  const defaultPrefs = getDefaultPreferences();
  if (typeof window === 'undefined') return defaultPrefs;

  const storedPrefs = getStoredPreferences();
  // Объединяем с дефолтными, чтобы убедиться, что все ключи есть,
  // и essential всегда true.
  return {
    ...defaultPrefs,
    ...(storedPrefs || {}), // Если storedPrefs null, возьмутся только defaultPrefs
    essential: true // Гарантируем, что essential всегда true
  };
};


export const saveConsents = (preferences) => {
  if (typeof window === 'undefined') return;

  const currentTimestamp = new Date().toISOString();
  const prefsToSave = {
    ...getAllConsents(), // Берем текущие значения, чтобы не потерять другие типы согласий, если они появятся
    ...preferences,      // Обновляем переданными значениями (analytics, advertising)
    essential: true,     // essential всегда true
    timestamp: currentTimestamp // Обновляем временную метку
  };

  try {
    localStorage.setItem('consent_preferences', JSON.stringify(prefsToSave));
    if (isDevelopment) console.log('[Consent] Preferences saved to localStorage:', prefsToSave);

    // Обновляем состояние согласия для внешних сервисов
    updatePosthogConsent(prefsToSave.analytics);
    updateGTMConsent(prefsToSave.analytics, prefsToSave.advertising);

  } catch (error) {
    console.error('[Consent] Error saving consent preferences to localStorage:', error);
  }
};

// --- PostHog Functions ---
let posthogInitialized = false; // Флаг для предотвращения повторной инициализации

export const initializePosthog = () => {
  if (typeof window === 'undefined' || posthogInitialized) return false;
  if (!POSTHOG_API_KEY) {
    if (isDevelopment) console.error('[PostHog] API Key is missing. PostHog cannot be initialized.');
    return false;
  }

  // Проверяем, был ли PostHog уже полностью загружен и инициализирован
  if (window.posthog && window.posthog.__loaded) {
     if (isDevelopment) console.log('[PostHog] Already initialized and loaded. Applying current consent state.');
     updatePosthogConsent(hasConsent('analytics')); // Убедимся, что состояние согласия актуально
     posthogInitialized = true;
     return true;
  }
  // Предотвращаем повторный вызов init, если он уже был вызван, но __loaded еще false
  if (window.posthog && window.posthog._is_initialized) { // _is_initialized - внутренний флаг
      if (isDevelopment) console.log('[PostHog] init() already called, waiting for "loaded" callback or already loaded.');
      return true;
  }


  const userPreviouslyMadeChoice = hasUserMadeChoice();
  const analyticsPreviouslyEnabled = hasConsent('analytics'); // Используем hasConsent для проверки

  const initialPersistenceType = (userPreviouslyMadeChoice && analyticsPreviouslyEnabled) ? 'localStorage' : 'memory';

  if (isDevelopment) {
    console.log(`[PostHog] Initializing PostHog. Previously chose: ${userPreviouslyMadeChoice}, Previously consented analytics: ${analyticsPreviouslyEnabled}. Initial persistence: ${initialPersistenceType}`);
  }

  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_API_HOST,
    person_profiles: 'identified_only',
    capture_pageview: false, // Управляем вручную
    persistence: initialPersistenceType,
    autocapture: false,
    debug: isDevelopment,
    loaded: function(posthogInstance) {
      if (isDevelopment) console.log('[PostHog] Library loaded successfully.');
      posthogInitialized = true; // Отмечаем, что библиотека полностью загружена

      const currentAnalyticsConsent = hasConsent('analytics'); // Перепроверяем актуальное согласие
      if (currentAnalyticsConsent) {
        if (isDevelopment) console.log('[PostHog] Callback "loaded": Opting IN and capturing pageview.');
        posthogInstance.opt_in_capturing({
          capture_event_name: '$pageview', // Захватить $pageview при opt-in
          persistence_type: 'localStorage',
          enable_persistence: true
        });
        // posthogInstance.capture('$pageview'); // $pageview уже захвачен через capture_event_name
      } else {
        if (isDevelopment) console.log('[PostHog] Callback "loaded": Opting OUT.');
        posthogInstance.opt_out_capturing();
      }
    }
  });
  return true;
};

export const updatePosthogConsent = (enabled) => {
  if (typeof window === 'undefined' || !window.posthog) {
    if (isDevelopment && !posthogInitialized) console.warn('[PostHog] updatePosthogConsent called before PostHog fully loaded. Consent will be applied once loaded.');
    // Если PostHog еще не загружен, он применит согласие в колбэке `loaded`.
    // Если уже загружен, то применяем немедленно.
    if (!window.posthog || !window.posthog.__loaded) return; // Проверяем __loaded для уверенности
    return;
  }

  if (enabled) {
    if (isDevelopment) console.log('[PostHog] Consent changed to ENABLED: Opting IN and capturing pageview.');
    window.posthog.opt_in_capturing({
        capture_event_name: '$pageview', // Важно, чтобы $pageview захватывался при включении
        persistence_type: 'localStorage',
        enable_persistence: true
    });
    // window.posthog.capture('$pageview'); // Захватывается через capture_event_name
  } else {
    if (isDevelopment) console.log('[PostHog] Consent changed to DISABLED: Opting OUT.');
    window.posthog.opt_out_capturing();
  }
};

// --- GTM Consent Function ---
export const updateGTMConsent = (analytics, advertising) => {
  if (typeof window === 'undefined' || !window.gtag) {
    if (isDevelopment) console.warn('[Consent] GTM (window.gtag) not available for consent update.');
    return;
  }

  const consentState = {
    'analytics_storage': analytics ? 'granted' : 'denied',
    'ad_storage': advertising ? 'granted' : 'denied',
    'ad_user_data': advertising ? 'granted' : 'denied', // Связано с согласием на рекламу
    'ad_personalization': advertising ? 'granted' : 'denied', // Связано с согласием на рекламу
    // 'personalization_storage': analytics ? 'granted' : 'denied', // Если у вас есть такой тип
    // 'functionality_storage': 'granted', // Обычно не меняется пользователем
    // 'security_storage': 'granted',      // Обычно не меняется пользователем
  };

  window.gtag('consent', 'update', consentState);
  if (isDevelopment) console.log('[Consent] GTM Consent Updated:', consentState);
};

// ------------------------
// React Component: ConsentManager
// ------------------------
export function ConsentManager() {
  const [consentBannerVisible, setConsentBannerVisible] = useState(false);
  const [settingsViewVisible, setSettingsViewVisible] = useState(false);
  const [currentPreferences, setCurrentPreferences] = useState(getDefaultPreferences()); // Локальное состояние для UI настроек

  const [expandedSections, setExpandedSections] = useState({
    analytics: false,
    advertising: false
  });

  // Инициализация и проверка при монтировании
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // PostHog инициализируется здесь, при первом рендере ConsentManager
    initializePosthog();

    if (hasUserMadeChoice()) {
      setConsentBannerVisible(false); // Баннер не показываем, если выбор сделан
      // Загружаем актуальные согласия для возможного отображения в настройках, если пользователь их откроет
      setCurrentPreferences(getAllConsents());
      // GTM согласие уже должно быть обновлено в _app.js на основе сохраненных данных
    } else {
      setConsentBannerVisible(true); // Показываем баннер, если выбора еще не было
      setCurrentPreferences(getDefaultPreferences()); // В настройках будут значения по умолчанию
    }
  }, []);

  const applyConsentAndSave = useCallback((analytics, advertising) => {
    const newPreferences = { ...currentPreferences, analytics, advertising };
    saveConsents(newPreferences); // Эта функция сохраняет и вызывает updatePosthogConsent/updateGTMConsent

    setConsentBannerVisible(false);
    setSettingsViewVisible(false);
    setCurrentPreferences(getAllConsents()); // Обновляем локальное состояние UI
  }, [currentPreferences]);

  const handleAcceptAll = () => applyConsentAndSave(true, true);
  const handleRejectAll = () => {
    // При Reject All мы все равно сохраняем essential: true
    // saveConsents сама позаботится об этом
    applyConsentAndSave(false, false);
  };

  const handleOpenSettings = () => {
    setCurrentPreferences(getAllConsents()); // Загружаем актуальные согласия перед показом настроек
    setSettingsViewVisible(true);
    // consentBannerVisible остается true, если было true
  };

  const handleCloseSettings = () => {
    setSettingsViewVisible(false);
    // Если пользователь закрыл настройки без сохранения и до этого не делал выбор,
    // основной баннер (если был видим) должен остаться.
    // Если выбор уже был сделан, то consentBannerVisible будет false.
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleTogglePreference = (type) => {
    setCurrentPreferences(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const handleConfirmSettings = () => {
    applyConsentAndSave(currentPreferences.analytics, currentPreferences.advertising);
  };

  // getToggleSwitchClasses и renderCookieSection без существенных изменений,
  // но убедись, что они используют currentPreferences для отображения состояния
   const getToggleSwitchClasses = (enabled) => ({
     container: `w-10 h-5 rounded-full relative ${enabled ? 'bg-black' : 'bg-gray-300'} cursor-pointer`,
     slider: `absolute w-4 h-4 rounded-full bg-white top-0.5 transition-all ${enabled ? 'right-0.5' : 'left-0.5'}`
  });

  const renderCookieSection = (type, label, description) => {
     const isExpanded = expandedSections[type];
     const isEnabled = currentPreferences[type]; // Используем currentPreferences
     const toggleClasses = getToggleSwitchClasses(isEnabled);

     return (
       <div className="py-4">
         <div className="flex justify-between items-center">
           <div className="flex items-center cursor-pointer" onClick={() => toggleSection(type)}>
             <button className={TOGGLE_BUTTON_CLASSES}>{isExpanded ? '−' : '+'}</button>
             <span className="pl-2 text-sm">{label}</span>
           </div>
           <div className="flex items-center">
             <span className={`mr-2 text-sm ${isEnabled ? 'text-black' : 'text-gray-400'}`}>{isEnabled ? 'Enabled' : 'Disabled'}</span>
             <div onClick={() => handleTogglePreference(type)} className={toggleClasses.container}>
               <div className={toggleClasses.slider} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}></div>
             </div>
           </div>
         </div>
         {isExpanded && (<div className="mt-2 text-gray-600 pl-8 text-sm">{description}</div>)}
       </div>
     );
  };


  if (typeof window === 'undefined') {
    return null; // Ничего не рендерим на сервере
  }

  // Логика отображения баннера или настроек
  if (!consentBannerVisible && !settingsViewVisible) {
    return null; // Ничего не показываем, если выбор сделан и настройки не открыты
  }

  return (
    <>
      {/* Основной баннер согласия */}
      {consentBannerVisible && !settingsViewVisible && (
        <div className={CONTAINER_CLASSES}><div className={BANNER_CLASSES}><div className="p-6">
          <h2 className="text-lg font-bold mb-1">Cookies managing</h2>
          <p className="mb-6 text-sm">We use cookies to provide the best site experience. By continuing to use the site, you consent to our cookie policy.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={handleAcceptAll} className={`${BUTTON_PRIMARY_CLASSES} w-full sm:w-auto`}>Accept All</button>
            <button onClick={handleRejectAll} className={`${BUTTON_OUTLINE_CLASSES} w-full sm:w-auto`}>Reject All</button>
            <button onClick={handleOpenSettings} className={`${BUTTON_SECONDARY_CLASSES} mt-2 sm:mt-0 sm:ml-auto`}>Cookie Settings</button>
          </div>
        </div></div></div>
      )}

      {/* Экран настроек */}
      {settingsViewVisible && ( // Показываем настройки независимо от consentBannerVisible, если settingsViewVisible is true
        <div className={CONTAINER_CLASSES}><div className={BANNER_CLASSES}>
          <div className="flex items-center p-4 pl-6 mb-0 border-b">
            <button onClick={handleCloseSettings} className="text-gray-500 mr-3 p-1 hover:bg-gray-100 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <span className="text-base font-semibold">Cookie Settings</span>
          </div>
          <div className="px-6 pt-4 pb-6">
            <p className="mb-4 text-sm text-gray-700">Manage your cookie preferences. Essential cookies are always enabled for site functionality.</p>
            <div className="space-y-1">
              <div className="py-4 border-b"><div className="flex justify-between items-center">
                <div className="flex items-center"><span className="pl-2 text-sm font-medium">Essential cookies</span></div>
                <span className="text-green-600 font-semibold text-sm">Always Enabled</span>
              </div>
               {/* Можно добавить описание для Essential cookies, если нужно */}
              {/* <p className="mt-1 text-gray-600 pl-2 text-xs">These cookies are necessary for the website to function and cannot be switched off in our systems.</p> */}
              </div>
              {renderCookieSection('analytics', 'Analytics cookies', 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They allow us to measure and improve the performance of our site.')}
              {renderCookieSection('advertising', 'Advertising cookies', 'These cookies are used to track visitors across websites. They are set to display targeted advertisements based on your interests and online behavior. They also help measure the effectiveness of advertising campaigns.')}
            </div>
            <div className="mt-8">
                <button onClick={handleConfirmSettings} className={`${BUTTON_PRIMARY_CLASSES} w-full`}>Confirm My Choices</button>
            </div>
          </div>
        </div></div>
      )}
    </>
  );
}

// Экспортируем функции и компонент
export default {
  hasConsent,
  hasUserMadeChoice,
  getAllConsents,
  saveConsents,
  initializePosthog,
  // updatePosthogConsent, // Обычно не нужно экспортировать, т.к. вызывается из saveConsents
  // updateGTMConsent, // Обычно не нужно экспортировать, т.к. вызывается из saveConsents
  ConsentManager,
};